// Image generation settings — full port of the old web/settings page
// for image-gen. Quality / Style / Aspect chips drive the user's
// default for every gen + inpaint call; the Advanced section exposes
// the workflow overrides the server accepts (negative, positive
// suffix, steps, cfg, sampler, scheduler).
//
// Saves via imageGen.updateSettings (the same RPC the Studio composer
// uses for its quick-pick chips). Both surfaces share state, so a
// chip changed here updates the Studio composer next time it mounts.

import { useEffect, useState } from "preact/hooks";
import { Icon } from "../../icons";
import { api, type ImageGenOptions, type ImageGenPrefs } from "../../lib/rpc";

interface SettingsImageGenProps {
  notify: (msg: string) => void;
}

interface AdvancedOverrides {
  negative: string;
  positiveSuffix: string;
  steps: string;
  cfg: string;
  sampler: string;
  scheduler: string;
}

const EMPTY_ADV: AdvancedOverrides = {
  negative: "",
  positiveSuffix: "",
  steps: "",
  cfg: "",
  sampler: "",
  scheduler: "",
};

export function SettingsImageGen({ notify }: SettingsImageGenProps) {
  const [options, setOptions] = useState<ImageGenOptions | null>(null);
  const [prefs, setPrefs] = useState<ImageGenPrefs | null>(null);
  const [adv, setAdv] = useState<AdvancedOverrides>(EMPTY_ADV);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    api.imageGenListOptions().then(setOptions).catch(() => {});
    api.imageGenGetSettings().then((p) => {
      setPrefs(p);
      const a = (p.advanced || {}) as Record<string, unknown>;
      setAdv({
        negative: typeof a.negative === "string" ? a.negative : "",
        positiveSuffix: typeof a.positiveSuffix === "string" ? a.positiveSuffix : "",
        steps: a.steps != null ? String(a.steps) : "",
        cfg: a.cfg != null ? String(a.cfg) : "",
        sampler: typeof a.sampler === "string" ? a.sampler : "",
        scheduler: typeof a.scheduler === "string" ? a.scheduler : "",
      });
      // Auto-open the advanced section when any override exists, so
      // the user sees their existing customization without hunting.
      if (Object.values(a).some((v) => v !== undefined && v !== null && v !== "")) {
        setAdvancedOpen(true);
      }
    }).catch(() => {});
  }, []);

  const setChip = (kind: "quality" | "style" | "aspect", value: string) => {
    if (!prefs) return;
    setPrefs({ ...prefs, [kind]: value });
  };

  const save = async () => {
    if (!prefs || saving) return;
    setSaving(true);
    try {
      // Only ship the fields that have a value — empty strings stay
      // out so the server's per-field validator doesn't reject them
      // (steps/cfg in particular need to be numeric or omitted).
      const cleaned: Record<string, unknown> = {};
      if (adv.negative.trim())       cleaned.negative = adv.negative.trim();
      if (adv.positiveSuffix.trim()) cleaned.positiveSuffix = adv.positiveSuffix.trim();
      if (adv.steps.trim()) {
        const n = Number(adv.steps.trim());
        if (Number.isFinite(n)) cleaned.steps = n;
      }
      if (adv.cfg.trim()) {
        const n = Number(adv.cfg.trim());
        if (Number.isFinite(n)) cleaned.cfg = n;
      }
      if (adv.sampler.trim())   cleaned.sampler   = adv.sampler.trim();
      if (adv.scheduler.trim()) cleaned.scheduler = adv.scheduler.trim();

      await api.imageGenUpdateSettings({
        quality: prefs.quality,
        style: prefs.style,
        aspect: prefs.aspect,
        advanced: Object.keys(cleaned).length > 0 ? cleaned : null,
      });
      setSavedAt(Date.now());
      notify("Settings saved");
    } catch (e: any) {
      notify(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const renderChips = (
    kind: "quality" | "style" | "aspect",
    enums: Record<string, { label: string; description: string | null }>,
    active: string,
  ) => (
    <div className="settings-chip-row">
      {Object.entries(enums).map(([key, { label, description }]) => (
        <button
          key={key}
          type="button"
          className="settings-chip"
          data-active={active === key}
          onClick={() => setChip(kind, key)}
          title={description || undefined}
        >
          {label}
        </button>
      ))}
    </div>
  );

  if (!options || !prefs) {
    return (
      <div className="settings-section">
        <h3 className="settings-section-title">Image generation</h3>
        <p className="settings-section-sub">Loading…</p>
      </div>
    );
  }

  const justSaved = savedAt && Date.now() - savedAt < 2500;

  return (
    <div className="settings-section">
      <h3 className="settings-section-title">Image generation</h3>
      <p class="settings-section-sub">
        Defaults applied to every gen + inpaint in Studio. The Advanced
        section is for power-user workflow overrides (steps, sampler,
        etc.) — leave blank to use the server preset.
      </p>

      <div className="settings-row">
        <label className="settings-row-label">Quality</label>
        {renderChips("quality", options.quality, prefs.quality)}
      </div>
      <div className="settings-row">
        <label className="settings-row-label">Style</label>
        {renderChips("style", options.style, prefs.style)}
      </div>
      <div className="settings-row">
        <label className="settings-row-label">Aspect</label>
        {renderChips("aspect", options.aspect, prefs.aspect)}
      </div>

      <button
        className="settings-advanced-toggle"
        onClick={() => setAdvancedOpen((v) => !v)}
        type="button"
      >
        <Icon name={advancedOpen ? "x" : "plus"} size={12} /> Advanced overrides
      </button>
      {advancedOpen && (
        <div className="settings-advanced">
          <div className="settings-row">
            <label className="settings-row-label">Negative prompt</label>
            <input
              className="input"
              placeholder="watermark, blurry, lowres…"
              value={adv.negative}
              onInput={(e) => setAdv({ ...adv, negative: (e.currentTarget as HTMLInputElement).value })}
            />
          </div>
          <div className="settings-row">
            <label className="settings-row-label">Positive suffix</label>
            <input
              className="input"
              placeholder="appended to every prompt — e.g. 'cinematic lighting, 35mm'"
              value={adv.positiveSuffix}
              onInput={(e) => setAdv({ ...adv, positiveSuffix: (e.currentTarget as HTMLInputElement).value })}
            />
          </div>
          <div className="settings-grid-two">
            <div className="settings-row">
              <label className="settings-row-label">Steps (1–100)</label>
              <input
                className="input" type="number" min="1" max="100"
                placeholder="preset default"
                value={adv.steps}
                onInput={(e) => setAdv({ ...adv, steps: (e.currentTarget as HTMLInputElement).value })}
              />
            </div>
            <div className="settings-row">
              <label className="settings-row-label">CFG (0–30)</label>
              <input
                className="input" type="number" min="0" max="30" step="0.1"
                placeholder="preset default"
                value={adv.cfg}
                onInput={(e) => setAdv({ ...adv, cfg: (e.currentTarget as HTMLInputElement).value })}
              />
            </div>
          </div>
          <div className="settings-grid-two">
            <div className="settings-row">
              <label className="settings-row-label">Sampler</label>
              <input
                className="input"
                placeholder="e.g. euler"
                value={adv.sampler}
                onInput={(e) => setAdv({ ...adv, sampler: (e.currentTarget as HTMLInputElement).value })}
              />
            </div>
            <div className="settings-row">
              <label className="settings-row-label">Scheduler</label>
              <input
                className="input"
                placeholder="e.g. simple, karras"
                value={adv.scheduler}
                onInput={(e) => setAdv({ ...adv, scheduler: (e.currentTarget as HTMLInputElement).value })}
              />
            </div>
          </div>
        </div>
      )}

      <div className="settings-foot">
        <button className="btn btn-primary" onClick={save} disabled={saving} type="button">
          <Icon name="check" size={13} />
          {saving ? "Saving…" : justSaved ? "Saved" : "Save settings"}
        </button>
      </div>
    </div>
  );
}
